<template>
  <div class="list-view submittal-tab">
    <o-loading :full-page="true" :active="isLoading" :can-cancel="false" />
    <mf-table
      ref="submittalTable"
      :key="`submittals-private-${submittalId}`"
      :table-props="tableProps"
      :load-data="loadData"
      :hide-gutter="true"
      :screen-table="true"
      table-name="submittalsPrivateTable"
      @cell-clicked="onCellClicked"
      @opened-row="getDetailRowOrder($event)"
    >
      <template #type="{ rowData }">
        {{ rowData.__t === 'Sourcing' ? 'Materials' : rowData.__t }}
      </template>
      <template #lastModified="{ rowData }">
        {{ $filters.usaDate($_.get(rowData, 'lastModified.at', '')) }}
      </template>
      <template #company="{ rowData }">
        {{ $_.get(rowData, 'owner.company.name', '') }}
      </template>
      <template #project="{ rowData }">
        {{ $_.get(rowData, 'project.name', '') }}
      </template>
      <template #plannedHrs="{ rowData }">
        <span class="is-size-4 is-pulled-right">
          {{ $filters.clockDisplay($_.get(rowData, 'manager.stats.plannedHrs', 0)) }}
        </span>
      </template>
      <template #actualHrs="{ rowData }">
        <span class="is-size-4 is-pulled-right">
          {{ $filters.clockDisplay($_.get(rowData, 'manager.stats.actualHrs', 0)) }}
        </span>
      </template>
      <template #lmvIcon="{ rowData, rowField, isHeader, rowIndex }">
        <IconField
          :rowData="rowData"
          :rowField="rowField"
          :isHeader="isHeader"
          :rowIndex="rowIndex"
          :projects="allProjects"
        />
      </template>
    </mf-table>
    <activity-log
      v-if="activityModalOpen"
      :isActive="activityModalOpen"
      :id="activityDetails.id"
      :projectId="activityDetails.projectId"
      :orderName="activityDetails.orderName"
      v-bind="({ leastStage, stage } = activityDetails)"
      @update:isActive="activityModalOpen = false"
    ></activity-log>
    <pause-restart-modal
      v-if="isPauseRestartActive"
      :is-active="isPauseRestartActive"
      :row-data="selectedOrder"
      @close="closePauseRestartModal"
    />
    <transition name="slide">
      <notes-icon-slider
        v-if="isSlideNotesActive"
        :isActive="isSlideNotesActive"
        :rowData="selectedOrder"
        @close="closeNotesSlider"
        @update:note="updateNotes($event)"
      >
      </notes-icon-slider>
    </transition>
    <transition name="slide">
      <item-document-slider
        v-if="isFileListVisible"
        :isActive="isFileListVisible"
        :rowData="selectedItem"
        :rowField="rowField"
        @close="closeDocumentSlider"
        :card="detailRowOrder"
        >
      </item-document-slider>
    </transition>
  </div>
</template>

<script>
import {
  defineComponent, reactive, toRefs, ref, onMounted, onBeforeUnmount, inject,
} from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import _ from 'lodash';
import SupplyChain from '@/models/SupplyChain';
import Projects from '@/models/Projects';
import ProductionStatusCols from '@/components/table-cols/ProductionStatusCols';
import MfTable from '@/components/table-fields/MfTable.vue';
import PauseRestartmodal from '@/components/modals/PauseRestartmodal.vue';
import ActivityLog from '@/components/ActivityLog.vue';
import IconField from '@/components/fields/IconField.vue';
import CardEditMixin from '@/components/mixins/CardEditMixin';
import NotesIconSlider from '@/components/modals/NotesIconSlider.vue';
import ItemDocumentSlider from '@/components/modals/ItemDocumentSlider.vue';

export default defineComponent({
  name: 'MfSubmittalPrivateView',
  components: {
    MfTable,
    IconField,
    ActivityLog,
    'pause-restart-modal': PauseRestartmodal,
    'notes-icon-slider': NotesIconSlider,
    'item-document-slider': ItemDocumentSlider,
  },
  props: {
    submittalId: { type: String, default: '' },
  },
  setup(props) {
    const store = useStore();
    const toast = useToast();
    const emitter = inject('emitter');
    const submittalTable = ref(null);
    const {
      printQr, openLmvModel, openWithUserPref, archiveCard, getRestartPermission,
    } = CardEditMixin();

    const tableProps = _.cloneDeep(ProductionStatusCols);
    tableProps.toolBar = {
      ...tableProps.toolBar,
      bulkActions: () => [],
      buttons: () => [],
      hideSearchBar: true,
    };
    const data = reactive({
      tableProps,
      isLoading: false,
      isPauseRestartActive: false,
      isSlideNotesActive: false,
      selectedOrder: {},
      activityModalOpen: false,
      activityDetails: {
        id: '',
        projectId: '',
        orderName: '',
      },
      allProjects: [],
      isFileListVisible: false,
      selectedItem: {},
      rowField: {},
      detailRowOrder: {},
    });

    const loadData = async (commonParams = {}) => {
      data.isLoading = true;
      try {
        const response = await SupplyChain.supplyChain({
          projectId: store.getters.selectedIdsForKey('projects'),
          module: ['Prefabs', 'ProductionOrder', 'Materials'],
          screen: 'submittals-private',
          location: store.getters.selectedIdsForKey('locations', false),
          company: store.getters.selectedIdsForKey('companies', false),
          owner: store.getters.selectedOwners,
          stage: store.getters.selectedValuesForKey('orderStage', false),
          status: store.getters.selectedValuesForKey('orderStatus', false),
          submittals: props.submittalId,
          ...store.getters.selectedDatesForKeys([
            'deliverStartDate', 'deliverEndDate', 'coordStartDate', 'coordEndDate',
            'detailByStartDate', 'detailByEndDate', 'manufactureByStartDate',
            'manufactureByEndDate', 'qaByStartDate', 'qaByEndDate',
          ]),
          search: store.state.queryParams.searchText,
          showPrivate: true,
          ...commonParams,
        });
        console.log('responsle', response);
        return response;
      } finally {
        data.isLoading = false;
      }
    };

    const refreshTable = async () => {
      await submittalTable.value?.refreshTable();
    };

    const saveRow = async (rowData) => {
      let order = _.cloneDeep(rowData);
      try {
        order = await order.slimSave();
        _.set(rowData, 'isEditing', !rowData.isEditing);
        Object.assign(rowData, order);
      } catch (e) {
        const msg = _.get(e.data, 'message', '')
          || _.get(e.data, 'msg', '') || _.get(e, 'message', '')
          || 'Error saving: please contact ManufactOn support';
        toast.error(msg);
      }
    };

    const onCellClicked = async (event) => {
      const { type, dataObj } = event;
      if (['pause', 'restart', 'pauseRestart'].includes(type)) {
        data.selectedOrder = dataObj;
        const cannotRestart = await getRestartPermission(dataObj);
        if (dataObj.status === 'pause' && cannotRestart) {
          toast.error('Only Project admins can restart the order');
          return;
        }
        data.isPauseRestartActive = true;
      } else if (type === 'openCard') {
        openWithUserPref(dataObj, 'submittals-private', refreshTable);
      } else if (type === 'activity') {
        data.activityModalOpen = true;
        data.activityDetails.id = dataObj._id;
        data.activityDetails.projectId = dataObj.project._id;
        data.activityDetails.orderName = dataObj.name;
        data.activityDetails.leastStage = dataObj.leastStage;
        data.activityDetails.stage = dataObj.stage;
      } else if (type === 'printQr') {
        printQr(dataObj);
      } else if (type === 'lmv') {
        openLmvModel(dataObj);
      } else if (type === 'remove') {
        await archiveCard(dataObj, refreshTable);
      } else if (type === 'save') {
        await saveRow(dataObj);
      }
    };

    const closePauseRestartModal = async () => {
      data.isPauseRestartActive = false;
      await refreshTable();
    };

    const closeNotesSlider = () => {
      data.isSlideNotesActive = false;
      data.selectedOrder = {};
    };

    const closeDocumentSlider = () => {
      data.isFileListVisible = false;
    };

    const getDetailRowOrder = (e) => {
      data.detailRowOrder = e;
    };

    const updateNotes = async (event) => {
      if (event.type === 'save' && !event.isItem) {
        await saveRow(event.data);
      }
    };

    emitter.on('toggle:notesSlider', (payload) => {
      data.selectedOrder = payload.data;
      data.isSlideNotesActive = payload.isActive;
    });

    emitter.on('toggle:itemDocsSlider', (payload) => {
      data.selectedItem = payload.data;
      data.rowField = payload.rowField;
      data.isFileListVisible = payload.isActive;
    });

    onBeforeUnmount(() => {
      emitter.off('toggle:notesSlider');
      emitter.off('toggle:itemDocsSlider');
    });

    onMounted(async () => {
      data.allProjects = await Projects.allProjects();
    });

    return {
      ...toRefs(data),
      submittalTable,
      loadData,
      onCellClicked,
      closePauseRestartModal,
      closeNotesSlider,
      updateNotes,
      closeDocumentSlider,
      getDetailRowOrder,
    };
  },
});
</script>

